import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';

import styled from 'styled-components';

import { getApartmentTypes } from 'apis/SettingsAPI';
import { isCommunityCCRC } from 'components/Community/utils';
import AggregationTypePicker from 'components/lib/AggregationTypePicker';
import { DISPLAY_VALUES, ORIENTATION_TYPE } from 'constants/charts';
import { AREA_UNIT_LABEL } from 'constants/units';
import { DataType } from 'store/slices/globalSlice';
import { isOverallApartmentType } from 'utils/data/apartmentTypes';
import { getAreaUnitLabel } from 'utils/helpers/units';

import CommunityViewWidgetModalAmenitiesFilter from './CommunityViewWidgetModalAmenitiesFilter';
import CommunityViewWidgetModalCommunityContractFilter from './CommunityViewWidgetModalCommunityContractFilter';
import { useCommunityViewWidgetModalContext } from './CommunityViewWidgetModalContext';
import CommunityViewWidgetModalContractBenefitsFilter from './CommunityViewWidgetModalContractBenefitsFilter';
import CommunityViewWidgetModalEditCategories from './CommunityViewWidgetModalEditCategories';
import CommunityViewWidgetModalFilter from './CommunityViewWidgetModalFilter';
import CommunityViewWidgetModalFilterSingleSelect from './CommunityViewWidgetModalFilterSingleSelect';
import CommunityViewWidgetModalGroupingFilter from './CommunityViewWidgetModalGroupingFilter';
import {
  GRAPH_WIDGET_TYPES,
  GROUPING_WIDGET_TYPES,
  WIDGET_FILTER_OPTIONS,
  WIDGET_FILTER_TYPES,
  WIDGET_FILTERS,
  WIDGET_TYPES,
} from '../../../common/Widgets/constants';
import { getDefaultGroupByForWidgetType } from '../../../common/Widgets/GraphWidget/utils';
import { WidgetGrouping } from '../../../common/Widgets/types';
import { getWidgetTypeLabel } from '../../../common/Widgets/utils';
import {
  DATA_DISPLAY_TYPE,
  DATA_GROUPING_TYPE,
  DATA_GROUPING_TYPE_LABELS,
  DATA_RANGE_TYPE,
  WIDGET_GROUPING_SQ_FT_CATEGORIES_DEFAULT,
} from '../../../common/Widgets/Widget/constants';
import { useAppSelector } from '../../../store/hook';
import { useCommunityViewContext } from '../CommunityViewContext';
import { AGGREGATION_TYPE_LABELS } from '../constants';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 225px;
  padding: 20px;
  border-right: 1px solid var(--line-gray);
  gap: 10px;
  overflow-y: auto;
  max-height: 625px;
`;

const MapText = styled.span`
  margin: 10px 0 0;
  font-size: 12px;
  color: var(--text-secondary);
`;

type Props = {
  marginTop?: string;
};

const Title = styled.div<Props>`
  font-family: var(--font-bold);
  font-size: 14px;
  margin-top: ${(props) => (props.marginTop ? props.marginTop : 'unset')};
`;

const CommunityViewWidgetModalFilters = () => {
  const { livingTypes = [], apartmentTypeGroups = [] } = useAppSelector((state) => state.global);
  const {
    type,
    careTypes,
    roomTypes,
    roomTypesPriceDisplay,
    careLevels,
    medicationLevels,
    reviewSources,
    reviewCategories,
    swotCategories,
    careLevelsStatsType,
    medicationLevelsStatsType,
    initialRange,
    initialFormat,
    initialOrientation,
    displayValue,
    groupBy,
    originalWidget,
    priceDisplay,
    setCareTypes,
    setRoomTypes,
    setRoomTypesPriceDisplay,
    setCareLevels,
    setMedicationLevels,
    setReviewSources,
    setReviewCategories,
    setSwotCategories,
    setCareLevelsStatsType,
    setMedicationLevelsStatsType,
    setInitialRange,
    setInitialFormat,
    setInitialOrientation,
    setDisplayValue,
    setGroupBy,
    setPriceDisplay,
  } = useCommunityViewWidgetModalContext();

  const { selectedCompany } = useAppSelector((state) => state.global);
  const { community } = useCommunityViewContext();
  const areaUnitLabel = getAreaUnitLabel(community);
  const [roomTypesPriceDisplayOption, setRoomTypesPriceDisplayOption] = useState<number>(roomTypesPriceDisplay);
  const [otherOptions, setOtherOptions] = useState<number>(careLevelsStatsType);
  const [medicationOtherOptions, setMedicationOtherOptions] = useState<number>(medicationLevelsStatsType);

  const isCommunityCcrc = isCommunityCCRC(community?.rental_types || []);

  const { data: apartmentTypes = [] } = useQuery({
    queryKey: ['apartmentTypes', 's', selectedCompany?.id],
    queryFn: () => getApartmentTypes({ companies: [selectedCompany?.id] }),
    refetchOnWindowFocus: false,
  });

  const reviewCategoriesOptions = useAppSelector((state) =>
    (state.global.reviewCategories ?? []).map((item) => ({
      label: item.label,
      value: item.id,
    }))
  );

  const widgetTypeOptions = [
    {
      label: getWidgetTypeLabel(type, community),
      value: type,
    },
  ];

  const careTypesOptions = livingTypes.map((el: any) => ({
    label: el?.type,
    value: el?.id,
  }));

  const roomTypesOptions = apartmentTypeGroups
    ?.filter((group) =>
      apartmentTypes?.some((apartmentType: DataType) =>
        group.apartmentTypes.find((item) => item?.id === apartmentType?.id && !isOverallApartmentType(item))
      )
    )
    .map((el: any) => ({
      label: el?.name,
      value: el?.id,
      options: el?.apartmentTypes.filter(
        (item: any) => !isOverallApartmentType(item) && apartmentTypes?.find((t: any) => t?.id === item?.id)
      ),
    }));

  const priceDisplayOptions = WIDGET_FILTER_OPTIONS[WIDGET_FILTER_TYPES.PRICE_DISPLAY].map((item) => ({
    label: item.name,
    value: item.id,
  }));
  const rangeOptions = [
    {
      value: DATA_RANGE_TYPE.NOW,
      label: DATA_RANGE_TYPE.NOW.charAt(0).toUpperCase() + DATA_RANGE_TYPE.NOW.slice(1),
    },
    {
      value: DATA_RANGE_TYPE.OVERTIME,
      label: DATA_RANGE_TYPE.OVERTIME.charAt(0).toUpperCase() + DATA_RANGE_TYPE.OVERTIME.slice(1),
    },
  ];
  if (GROUPING_WIDGET_TYPES.includes(type)) {
    rangeOptions.splice(1, 0, {
      value: DATA_RANGE_TYPE.GROUPED,
      label: DATA_RANGE_TYPE.GROUPED.charAt(0).toUpperCase() + DATA_RANGE_TYPE.GROUPED.slice(1),
    });
  }

  const careLevelStatsLabel = AGGREGATION_TYPE_LABELS[otherOptions];
  const medicationLevelStatsLabel = AGGREGATION_TYPE_LABELS[medicationOtherOptions];
  const roomTypesPriceDisplayLabel = AGGREGATION_TYPE_LABELS[roomTypesPriceDisplayOption];

  const isGraphWidget = GRAPH_WIDGET_TYPES.includes(type) && type !== WIDGET_TYPES.MARKET_POSITIONING;
  const showAdvancedWidgetSettings = isGraphWidget && type !== WIDGET_TYPES.SWOT_SCORE;
  const showOrientationFilter = isGraphWidget && initialRange === DATA_RANGE_TYPE.NOW;

  const setCareLevelsAndStatsType = (value: any) => {
    setCareLevels(value);
    setCareLevelsStatsType(otherOptions);
  };

  const setMedicationLevelsAndStatsType = (value: any) => {
    setMedicationLevels(value);
    setMedicationLevelsStatsType(medicationOtherOptions);
  };

  const setRoomTypesAndPriceDisplay = (value: any) => {
    setRoomTypes(value);
    setRoomTypesPriceDisplay(roomTypesPriceDisplayOption);
  };

  const setGrouping = (value: number) => {
    const grouping: WidgetGrouping = { type: value };
    if (value === DATA_GROUPING_TYPE.SQ_FT) {
      grouping.categories = originalWidget?.group_by?.categories?.length
        ? originalWidget.group_by.categories
        : WIDGET_GROUPING_SQ_FT_CATEGORIES_DEFAULT;
    }
    setGroupBy(grouping);
  };

  const getGroupingTypeLabel = (groupType: number) => {
    const label = DATA_GROUPING_TYPE_LABELS[groupType];
    if (groupType === DATA_GROUPING_TYPE.SQ_FT) {
      return label.replace(AREA_UNIT_LABEL.US, areaUnitLabel);
    }
    return label;
  };

  useEffect(() => {
    if (!groupBy && type) {
      const defaultGrouping = getDefaultGroupByForWidgetType(type);
      setGrouping(defaultGrouping.type);
    }
  }, [type, groupBy]);

  useEffect(() => {
    setOtherOptions(careLevelsStatsType);
    setRoomTypesPriceDisplayOption(roomTypesPriceDisplay);
    setMedicationOtherOptions(medicationLevelsStatsType);
  }, [careLevelsStatsType, medicationLevelsStatsType, roomTypesPriceDisplay]);

  return (
    <Container>
      <Title>Basic Settings</Title>
      <CommunityViewWidgetModalFilter
        title={'Widget Type'}
        disabled={true}
        defaultValue={[type]}
        options={widgetTypeOptions}
      />
      {WIDGET_FILTERS[type]?.includes(WIDGET_FILTER_TYPES.PRICE_DISPLAY) && (
        <CommunityViewWidgetModalFilterSingleSelect
          title="Price display"
          value={priceDisplay}
          options={priceDisplayOptions}
          onSelect={setPriceDisplay}
        />
      )}
      {WIDGET_FILTERS[type]?.includes(WIDGET_FILTER_TYPES.CARE_TYPE) && (
        <CommunityViewWidgetModalFilter
          title={'Care Types'}
          options={careTypesOptions}
          defaultValue={careTypes}
          onSelect={setCareTypes}
        />
      )}
      {WIDGET_FILTERS[type]?.includes(WIDGET_FILTER_TYPES.ROOM_TYPE) && (
        <CommunityViewWidgetModalGroupingFilter
          title={'Room Types'}
          options={roomTypesOptions}
          otherOptions={
            <AggregationTypePicker
              aggregationType={roomTypesPriceDisplayOption}
              label="PRICE DISPLAY"
              onSelect={setRoomTypesPriceDisplayOption}
            />
          }
          otherOptionsLabel={roomTypesPriceDisplayLabel}
          defaultValue={roomTypes}
          onSelect={(values: any) => setRoomTypesAndPriceDisplay(values)}
        />
      )}
      {WIDGET_FILTERS[type]?.includes(WIDGET_FILTER_TYPES.CARE_LEVEL) && (
        <CommunityViewWidgetModalFilter
          title={'Care Levels'}
          options={WIDGET_FILTER_OPTIONS[WIDGET_FILTER_TYPES.CARE_LEVEL].map((item: any) => ({
            label: item.name,
            value: item.id,
          }))}
          otherOptions={
            <AggregationTypePicker aggregationType={otherOptions} label="PRICE DISPLAY" onSelect={setOtherOptions} />
          }
          otherOptionsLabel={careLevelStatsLabel}
          defaultValue={careLevels}
          onSelect={setCareLevelsAndStatsType}
        />
      )}
      {WIDGET_FILTERS[type]?.includes(WIDGET_FILTER_TYPES.MEDICATION_LEVEL) && (
        <CommunityViewWidgetModalFilter
          title={'Medication Levels'}
          options={WIDGET_FILTER_OPTIONS[WIDGET_FILTER_TYPES.MEDICATION_LEVEL].map((item: any) => ({
            label: item.name,
            value: item.id,
          }))}
          otherOptions={
            <AggregationTypePicker
              aggregationType={medicationOtherOptions}
              label="PRICE DISPLAY"
              onSelect={setMedicationOtherOptions}
            />
          }
          otherOptionsLabel={medicationLevelStatsLabel}
          defaultValue={medicationLevels}
          onSelect={setMedicationLevelsAndStatsType}
        />
      )}
      {WIDGET_FILTERS[type]?.includes(WIDGET_FILTER_TYPES.REVIEW_SOURCE) && (
        <CommunityViewWidgetModalFilter
          title={'Review Source'}
          options={WIDGET_FILTER_OPTIONS[WIDGET_FILTER_TYPES.REVIEW_SOURCE].map((item: any) => ({
            label: item.name,
            value: item.id,
          }))}
          defaultValue={reviewSources}
          onSelect={setReviewSources}
        />
      )}
      {WIDGET_FILTERS[type]?.includes(WIDGET_FILTER_TYPES.REVIEW_CATEGORY) && (
        <CommunityViewWidgetModalFilter
          title={'Review Category'}
          options={reviewCategoriesOptions}
          defaultValue={reviewCategories}
          onSelect={setReviewCategories}
        />
      )}
      {WIDGET_FILTERS[type]?.includes(WIDGET_FILTER_TYPES.SWOT_CATEGORY) && (
        <CommunityViewWidgetModalFilter
          title={'SWOT Category'}
          options={WIDGET_FILTER_OPTIONS[WIDGET_FILTER_TYPES.SWOT_CATEGORY].map((item: any) => ({
            label: item.name,
            value: item.id,
          }))}
          defaultValue={swotCategories}
          onSelect={setSwotCategories}
        />
      )}
      {type === WIDGET_TYPES.MAP && (
        <MapText>
          {' '}
          The map widget shows your community and its competitors on an interactive map. You can filter out competitors
          using the filter on the main page.
        </MapText>
      )}
      {type === WIDGET_TYPES.AMENITIES && <CommunityViewWidgetModalAmenitiesFilter />}
      {type === WIDGET_TYPES.CONTRACT_BENEFITS && <CommunityViewWidgetModalContractBenefitsFilter />}
      {WIDGET_FILTERS[type]?.includes(WIDGET_FILTER_TYPES.COMMUNITY_CONTRACT_TYPE) && isCommunityCcrc && (
        <CommunityViewWidgetModalCommunityContractFilter />
      )}
      {showAdvancedWidgetSettings && (
        <>
          <Title marginTop="20px">Advanced Settings</Title>
          <CommunityViewWidgetModalFilterSingleSelect
            title="Initial selection"
            value={initialRange}
            options={rangeOptions}
            onSelect={setInitialRange}
          />
          {GROUPING_WIDGET_TYPES.includes(type) && (
            <CommunityViewWidgetModalFilterSingleSelect
              title={
                groupBy?.type === DATA_GROUPING_TYPE.SQ_FT ? <CommunityViewWidgetModalEditCategories /> : 'Grouping'
              }
              value={
                groupBy?.type ||
                (type === WIDGET_TYPES.RATING_GRAPH ? DATA_GROUPING_TYPE.REVIEW_SOURCE : DATA_GROUPING_TYPE.CARE_TYPE)
              }
              options={Object.values(DATA_GROUPING_TYPE)
                .filter((groupType) => {
                  if (type === WIDGET_TYPES.SQUARE_FOOTAGE_GRAPH) {
                    return [DATA_GROUPING_TYPE.ROOM_TYPE, DATA_GROUPING_TYPE.CARE_TYPE].includes(groupType);
                  }
                  if (type === WIDGET_TYPES.ENTRANCE_FEE_GRAPH) {
                    return [DATA_GROUPING_TYPE.ROOM_TYPE].includes(groupType);
                  }
                  if (
                    [
                      WIDGET_TYPES.MARKET_RATE_GRAPH,
                      WIDGET_TYPES.MARKET_RATE_SQUARE_FOOTAGE_GRAPH,
                      WIDGET_TYPES.MARKET_RATE_AVERAGE_CARE_COST_GRAPH,
                    ].includes(type)
                  ) {
                    return [
                      DATA_GROUPING_TYPE.ROOM_TYPE,
                      DATA_GROUPING_TYPE.SQ_FT,
                      DATA_GROUPING_TYPE.CARE_TYPE,
                    ].includes(groupType);
                  }

                  if (type === WIDGET_TYPES.RATING_GRAPH || type === WIDGET_TYPES.REVIEWS_SUMMARY) {
                    return [DATA_GROUPING_TYPE.REVIEW_SOURCE, DATA_GROUPING_TYPE.REVIEW_CATEGORY].includes(groupType);
                  }

                  return groupType === DATA_GROUPING_TYPE.CARE_TYPE;
                })
                .map((groupType) => ({
                  value: groupType,
                  label:
                    getGroupingTypeLabel(groupType).charAt(0).toUpperCase() + getGroupingTypeLabel(groupType).slice(1),
                }))}
              onSelect={setGrouping}
            />
          )}
          <CommunityViewWidgetModalFilterSingleSelect
            title="Initial format"
            value={initialFormat}
            options={[
              {
                value: DATA_DISPLAY_TYPE.GRAPH,
                label: DATA_DISPLAY_TYPE.GRAPH.charAt(0).toUpperCase() + DATA_DISPLAY_TYPE.GRAPH.slice(1),
              },
              {
                value: DATA_DISPLAY_TYPE.TABLE,
                label: DATA_DISPLAY_TYPE.TABLE.charAt(0).toUpperCase() + DATA_DISPLAY_TYPE.TABLE.slice(1),
              },
            ]}
            onSelect={setInitialFormat}
          />
        </>
      )}
      {showOrientationFilter && (
        <CommunityViewWidgetModalFilterSingleSelect
          title="Graph orientation"
          value={initialOrientation}
          options={[
            {
              value: ORIENTATION_TYPE.HORIZONTAL,
              label: ORIENTATION_TYPE.HORIZONTAL.charAt(0).toUpperCase() + ORIENTATION_TYPE.HORIZONTAL.slice(1),
            },
            {
              value: ORIENTATION_TYPE.VERTICAL,
              label: ORIENTATION_TYPE.VERTICAL.charAt(0).toUpperCase() + ORIENTATION_TYPE.VERTICAL.slice(1),
            },
          ]}
          onSelect={setInitialOrientation}
        />
      )}
      {isGraphWidget && (
        <CommunityViewWidgetModalFilterSingleSelect
          title="Display values"
          value={displayValue}
          options={[DISPLAY_VALUES.DISPLAY, DISPLAY_VALUES.DONT_DISPLAY]}
          onSelect={setDisplayValue}
        />
      )}
    </Container>
  );
};

export default CommunityViewWidgetModalFilters;
